// Home's registrations (lane B10 S16; loaded by views/b10/register.ts). One screen per widget key B1 defines whose data
// B10 gives (`app/reporting/dashboards.py` registers their providers through B1's `dashboard.register_widget`): kpis,
// req_health, search_readiness, timelines and reports, and — since B1 defines its key (D-25, C-B10-8) —
// active_requisitions. The plan's other extras, executive_link and disposition_worklist, stay absent until B1 defines
// their keys: their screens are kept here, unregistered. There is no #home view: Home is B1's, composed by `home.open`.
// Each screen registers with B1's Home (`views/home/widgets.tsx registerHomeWidget`, D-25: "Point Home registrations at
// registerHomeWidget. Every server widget key draws.") through `homeView`, the adapter from B10's view props to B1's
// element builder; a B10 view's type is `./host.ts`'s, and B10 keeps no Home registry of its own. Views for workbench,
// comp_queue and sync_health are their own lanes' (B5, B2, B11), a deviation from PLAN S16's list; hris_handoff, the
// key B1 defines for B9's handoff, is B10's binding of B9's records (S14b: HrisHandoff); panel_ack is S21's.

import type { ReactElement } from "react";
import type { WidgetOut } from "@/api/tools";
import { registerHomeWidget as registerOnHome, type HomeWidgetView as HomeElementView } from "@/views/home/widgets";
import type { HomeWidgetView } from "./host";
import { ActiveRequisitions } from "./ActiveRequisitions";
import { DispositionWorklist } from "./DispositionWorklist";
import { ExecutiveLink } from "./ExecutiveLink";
import { HrisHandoff } from "./HrisHandoff";
import { Kpis } from "./Kpis";
import { ReportsLinks } from "./ReportsLinks";
import { ReqHealth } from "./ReqHealth";
import { SearchReadiness } from "./SearchReadiness";
import { TimelinesPreview } from "./TimelinesPreview";

/** B1's widget keys whose data B10 gives, each with its screen. */
export const HOME_WIDGET_VIEWS: Readonly<Record<string, HomeWidgetView>> = {
  kpis: Kpis,
  req_health: ReqHealth,
  search_readiness: SearchReadiness,
  timelines: TimelinesPreview,
  reports: ReportsLinks,
};

/** B1's keys for another lane's records whose data B10 binds (S14b; `reporting/dashboards.py BOUND_KEYS`). */
export const BOUND_WIDGET_VIEWS: Readonly<Record<string, HomeWidgetView>> = {
  hris_handoff: HrisHandoff,
};

/** The plan's extras (C-B10-8): each registers on Home only once B1 defines its key (`ON_HOME` below). */
export const HELD_WIDGET_VIEWS: Readonly<Record<string, HomeWidgetView>> = {
  active_requisitions: ActiveRequisitions,
  executive_link: ExecutiveLink,
  disposition_worklist: DispositionWorklist,
};

/** The extras B1 defines (`app/dashboard.py` WIDGETS): active_requisitions (D-25). */
export const DEFINED_EXTRAS: readonly string[] = ["active_requisitions"];

type B10Props = { widgetKey: string; title: string; data: Record<string, unknown> };

/**
 * B10's view as B1's element builder. B10's screens are functions that call no hook at their top level (their hooks sit
 * in the bodies they draw) and return the design-system Widget element, or null to leave the widget out: the builder
 * calls the screen with the server's widget as its props and hands Home's grid the element it returns.
 */
export function homeView(view: HomeWidgetView): HomeElementView {
  if (typeof view !== "function" || (view.prototype as { isReactComponent?: unknown } | undefined)?.isReactComponent) {
    throw new Error("homeView: a B10 Home screen is a function component");
  }
  const draw = view as unknown as (props: B10Props) => ReactElement | null;
  return (widget: WidgetOut) => draw({ widgetKey: widget.key, title: widget.title, data: widget.data });
}

/** Every screen Home draws for B10, by key. */
export const ON_HOME: Readonly<Record<string, HomeWidgetView>> = {
  ...HOME_WIDGET_VIEWS,
  ...BOUND_WIDGET_VIEWS,
  ...Object.fromEntries(DEFINED_EXTRAS.map((key) => [key, HELD_WIDGET_VIEWS[key]!])),
};

for (const [key, view] of Object.entries(ON_HOME)) registerOnHome(key, homeView(view));
